// Birthday configuration of a club (`Club.birthdays`, task F1 of canchas-cumpleanos): the form state,
// its serialization for the club update request and the client-side validation. The back
// (helper/clubs/birthdaysConfig.js) stays the source of truth: these rules only mirror it so the
// organizer sees the problem next to the field instead of after a round trip.

export const BIRTHDAY_MODES = ["direct", "approval"];
export const BIRTHDAY_HOURS_OPTIONS = [12, 24, 48];
export const MAX_PACKAGES = 10;
export const MAX_WINDOWS = 14;
export const DURATION_STEP_MINUTES = 30;
export const MIN_DURATION_MINUTES = 30;
export const MAX_DURATION_MINUTES = 600;
export const MAX_PACKAGE_NAME = 60;
export const MAX_PACKAGE_INCLUDES = 120;
export const MAX_POLICY_TEXT = 1000;

export const QUESTION_KEYS = ["birthdayPerson", "age", "guests", "comments"];

// Same defaults as the back, so a club that predates the feature opens with the values it would get.
export const BIRTHDAY_DEFAULTS = {
  responseHours: 24,
  paymentHours: 24,
  minAdvanceHours: 72,
  maxAdvanceDays: 60,
  depositPercent: 30,
  refundDays: 7,
};

const TIME_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;

let keyCounter = 0;
// Client-only identity for a package row (React key); never sent to the back.
const nextKey = () => `bp-${(keyCounter += 1)}`;

export const newBirthdayPackage = () => ({
  _key: nextKey(),
  name: "",
  durationMinutes: 120,
  price: "",
  includes: "",
  active: true,
});

export const newBirthdayWindow = () => ({ weekdays: [6], start: "12:00", end: "20:00" });

export function defaultBirthdays() {
  return {
    enabled: false,
    mode: "approval",
    responseHours: BIRTHDAY_DEFAULTS.responseHours,
    paymentHours: BIRTHDAY_DEFAULTS.paymentHours,
    packages: [],
    courtIds: [],
    windows: [],
    minAdvanceHours: BIRTHDAY_DEFAULTS.minAdvanceHours,
    maxAdvanceDays: BIRTHDAY_DEFAULTS.maxAdvanceDays,
    depositPercent: BIRTHDAY_DEFAULTS.depositPercent,
    questions: Object.fromEntries(QUESTION_KEYS.map((key) => [key, true])),
    policy: { refundDays: BIRTHDAY_DEFAULTS.refundDays, text: "" },
  };
}

const numberOr = (value, fallback) => (value === undefined || value === null || value === "" ? fallback : Number(value));

/** Club payload (`club.birthdays`, possibly missing on old clubs) -> editable form state. */
export function normalizeBirthdays(raw) {
  const base = defaultBirthdays();
  if (!raw || typeof raw !== "object") return base;
  return {
    enabled: raw.enabled === true,
    mode: BIRTHDAY_MODES.includes(raw.mode) ? raw.mode : base.mode,
    responseHours: numberOr(raw.responseHours, base.responseHours),
    paymentHours: numberOr(raw.paymentHours, base.paymentHours),
    packages: (Array.isArray(raw.packages) ? raw.packages : []).map((p) => ({
      _key: nextKey(),
      _id: p._id ? String(p._id) : undefined,
      name: p.name || "",
      durationMinutes: numberOr(p.durationMinutes, 120),
      price: p.price ?? "",
      includes: p.includes || "",
      active: p.active !== false,
    })),
    courtIds: (Array.isArray(raw.courtIds) ? raw.courtIds : []).map((c) => String(c?._id || c)),
    windows: (Array.isArray(raw.windows) ? raw.windows : []).map((w) => ({
      weekdays: (Array.isArray(w.weekdays) ? w.weekdays : []).map(Number),
      start: w.start || "",
      end: w.end || "",
    })),
    minAdvanceHours: numberOr(raw.minAdvanceHours, base.minAdvanceHours),
    maxAdvanceDays: numberOr(raw.maxAdvanceDays, base.maxAdvanceDays),
    depositPercent: numberOr(raw.depositPercent, base.depositPercent),
    questions: Object.fromEntries(QUESTION_KEYS.map((key) => [key, raw.questions?.[key] !== false])),
    policy: {
      refundDays: numberOr(raw.policy?.refundDays, base.policy.refundDays),
      text: raw.policy?.text || "",
    },
  };
}

/**
 * Form state -> the whole config object the update request carries. The back replaces packages,
 * windows and courtIds as a whole and merges questions/policy key by key, so sending everything is
 * the round-trip that leaves nothing stale. A package keeps its `_id` so the back preserves it.
 */
export function buildBirthdaysPayload(birthdays) {
  const b = birthdays || defaultBirthdays();
  return {
    enabled: Boolean(b.enabled),
    mode: b.mode,
    responseHours: Number(b.responseHours),
    paymentHours: Number(b.paymentHours),
    packages: (b.packages || []).map((p) => ({
      ...(p._id ? { _id: p._id } : {}),
      name: String(p.name || "").trim(),
      durationMinutes: Number(p.durationMinutes),
      price: Number(p.price),
      includes: String(p.includes || "").trim(),
      active: p.active !== false,
    })),
    courtIds: [...(b.courtIds || [])],
    windows: (b.windows || []).map((w) => ({
      weekdays: [...(w.weekdays || [])].map(Number).sort((a, c) => a - c),
      start: w.start,
      end: w.end,
    })),
    minAdvanceHours: Number(b.minAdvanceHours),
    maxAdvanceDays: Number(b.maxAdvanceDays),
    depositPercent: Number(b.depositPercent),
    questions: Object.fromEntries(QUESTION_KEYS.map((key) => [key, b.questions?.[key] !== false])),
    policy: { refundDays: Number(b.policy?.refundDays), text: b.policy?.text || "" },
  };
}

/** 30 -> "30 min", 120 -> "2 h", 150 -> "2 h 30 min". */
export function formatDuration(minutes) {
  const total = Number(minutes);
  if (!Number.isFinite(total)) return "";
  const hours = Math.floor(total / 60);
  const rest = total % 60;
  if (hours === 0) return `${rest} min`;
  return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}

export const DURATION_OPTIONS = Array.from(
  { length: MAX_DURATION_MINUTES / DURATION_STEP_MINUTES },
  (_, i) => (i + 1) * DURATION_STEP_MINUTES
).map((value) => ({ value, label: formatDuration(value) }));

// "" (an empty input) and non-numeric text are not numbers, unlike Number("").
const wholeNumber = (value) => {
  if (typeof value === "string" && value.trim() === "") return NaN;
  const n = Number(value);
  return Number.isInteger(n) ? n : NaN;
};

const SECTION_NAMES = ["modo", "paquetes", "donde", "politica"];

/**
 * Mirrors the back's validation of `birthdays`. Returns the messages grouped by the section of the
 * tab that shows them (`modo`, `paquetes`, `donde`, `politica`); every list is empty
 * when the config is valid. A disabled draft may be incomplete; an enabled one has to be sellable.
 */
export function validateBirthdays(birthdays) {
  const errors = Object.fromEntries(SECTION_NAMES.map((name) => [name, []]));
  const b = birthdays || defaultBirthdays();

  if (!BIRTHDAY_HOURS_OPTIONS.includes(Number(b.responseHours))) {
    errors.modo.push("El plazo de respuesta tiene que ser 12, 24 o 48 horas");
  }
  if (!BIRTHDAY_HOURS_OPTIONS.includes(Number(b.paymentHours))) {
    errors.modo.push("El plazo de pago tiene que ser 12, 24 o 48 horas");
  }

  const packages = b.packages || [];
  if (packages.length > MAX_PACKAGES) errors.paquetes.push(`Podés cargar hasta ${MAX_PACKAGES} paquetes`);
  packages.forEach((p, index) => {
    const n = index + 1;
    const name = String(p.name || "").trim();
    if (!name) errors.paquetes.push(`El paquete ${n} necesita un nombre`);
    else if (name.length > MAX_PACKAGE_NAME) {
      errors.paquetes.push(`El nombre del paquete ${n} no puede superar los ${MAX_PACKAGE_NAME} caracteres`);
    }

    const duration = wholeNumber(p.durationMinutes);
    if (!(duration >= MIN_DURATION_MINUTES && duration <= MAX_DURATION_MINUTES)) {
      errors.paquetes.push(`La duración del paquete ${n} tiene que estar entre 30 minutos y 10 horas`);
    } else if (duration % DURATION_STEP_MINUTES !== 0) {
      errors.paquetes.push(`La duración del paquete ${n} tiene que ser múltiplo de ${DURATION_STEP_MINUTES} minutos`);
    }

    const price = typeof p.price === "string" && p.price.trim() === "" ? NaN : Number(p.price);
    if (!Number.isFinite(price) || price < 0) {
      errors.paquetes.push(`El precio del paquete ${n} tiene que ser un número mayor o igual a 0`);
    }

    if (String(p.includes || "").trim().length > MAX_PACKAGE_INCLUDES) {
      errors.paquetes.push(`Lo que incluye el paquete ${n} no puede superar los ${MAX_PACKAGE_INCLUDES} caracteres`);
    }
  });

  const windows = b.windows || [];
  if (windows.length > MAX_WINDOWS) errors.donde.push(`Podés cargar hasta ${MAX_WINDOWS} franjas horarias`);
  windows.forEach((w, index) => {
    const n = index + 1;
    if (!Array.isArray(w.weekdays) || w.weekdays.length === 0) {
      errors.donde.push(`La franja ${n} necesita al menos un día de la semana`);
    }
    if (!TIME_PATTERN.test(w.start || "") || !TIME_PATTERN.test(w.end || "")) {
      errors.donde.push(`Los horarios de la franja ${n} tienen que tener formato HH:mm`);
    } else if (w.start >= w.end) {
      // A window never crosses midnight: the end is later than the start of the same day.
      errors.donde.push(`En la franja ${n} el fin tiene que ser posterior al inicio`);
    }
  });

  const minAdvance = wholeNumber(b.minAdvanceHours);
  if (!(minAdvance >= 0)) {
    errors.donde.push("La anticipación mínima tiene que ser un número entero mayor o igual a 0");
  }
  const maxAdvance = wholeNumber(b.maxAdvanceDays);
  if (!(maxAdvance >= 1 && maxAdvance <= 365)) {
    errors.donde.push("La anticipación máxima tiene que ser un número entero entre 1 y 365");
  } else if (minAdvance >= 0 && maxAdvance * 24 <= minAdvance) {
    errors.donde.push("La anticipación máxima tiene que ser mayor que la mínima");
  }
  const deposit = wholeNumber(b.depositPercent);
  if (!(deposit >= 0 && deposit <= 100)) {
    errors.donde.push("El porcentaje de seña tiene que ser un número entero entre 0 y 100");
  }

  const refundDays = wholeNumber(b.policy?.refundDays);
  if (!(refundDays >= 0)) {
    errors.politica.push("Los días de devolución tienen que ser un número entero mayor o igual a 0");
  }
  if ((b.policy?.text || "").length > MAX_POLICY_TEXT) {
    errors.politica.push(`El texto de la política no puede superar los ${MAX_POLICY_TEXT} caracteres`);
  }

  if (b.enabled) {
    if (!packages.some((p) => p.active !== false)) {
      errors.paquetes.push("Para habilitar cumpleaños cargá al menos un paquete activo");
    }
    if ((b.courtIds || []).length === 0) errors.donde.push("Para habilitar cumpleaños elegí al menos una cancha");
    if (windows.length === 0) errors.donde.push("Para habilitar cumpleaños cargá al menos una franja horaria");
  }

  return errors;
}

export const hasBirthdayErrors = (errors) =>
  Boolean(errors) && Object.values(errors).some((list) => list.length > 0);
